import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import { Grid, FormControlLabel } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import { decipherError } from '../../../../../../utils/decipherError'
import { dataCatalog } from '../../../../../../api/coreData'
import SydSwitch from '../../../../../commonDesign/SydSwitch'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(1)
  },
  infoText: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  warningContainer: {
    marginTop: theme.spacing(2)
  },
  formControlLabel: {
    '& .MuiFormControlLabel-label': {
      paddingLeft: 'var(--padding-sm)'
    }
  }
}))

const useSubmitter = (dataSource, onComplete) => {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: modifyDatasourceDeprecated } = dataCatalog.useModifyDatasourceDeprecated()

  const onSubmit = useCallback(async (formData) => {
    const command = {
      datasourceId: dataSource.catalogDatasourceId,
      isDeprecated: formData.isDeprecated
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await modifyDatasourceDeprecated(command)
      if (result?.statusCode === 500) {
        setError('Something unexpected happened while updating the deprecated status')
        return
      }

      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [dataSource, onComplete, modifyDatasourceDeprecated])

  return {
    onSubmit,
    processing,
    error
  }
}

function ModifyDatasourceDeprecatedForm ({ dataSource, onCancel, onComplete }) {
  const classes = useStyles()
  const [isDeprecated, setIsDeprecated] = useState(dataSource.isDeprecated || false)
  const { onSubmit, processing, error } = useSubmitter(dataSource, onComplete)

  if (!dataSource) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Alert severity='error'>No data source provided</Alert>
        </Grid>
      </Grid>
    )
  }

  return (
    <div className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className={classes.infoText}>
            Mark this datasource as deprecated to indicate it should no longer be used.
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.switchContainer}>
            <FormControlLabel
              className={classes.formControlLabel}
              control={
                <SydSwitch
                  value={isDeprecated}
                  onChange={(e, newValue) => setIsDeprecated(newValue)}
                  disabled={processing}
                />
              }
              label={isDeprecated ? 'Deprecated' : 'Active'}
            />
          </div>
        </Grid>

        {isDeprecated && (
          <Grid item xs={12}>
            <div className={classes.warningContainer}>
              <Alert severity='warning'>
                Marking this datasource as deprecated will signal to users that it should no longer be used for new implementations.
              </Alert>
            </div>
          </Grid>
        )}

        {/* Actions */}
        <Grid item xs={12}>
          <SydModalActions>
            {error && <div className='__error'>{error}</div>}
            <SydButton
              disabled={processing}
              variant='ghost'
              size='md'
              onClick={onCancel}
            >
              Cancel
            </SydButton>
            <SydButton
              processing={processing}
              variant='primary'
              size='md'
              onClick={() => onSubmit({ isDeprecated })}
            >
              Save Changes
            </SydButton>
          </SydModalActions>
        </Grid>
      </Grid>
    </div>
  )
}

ModifyDatasourceDeprecatedForm.propTypes = {
  dataSource: PropTypes.shape({
    catalogDatasourceId: PropTypes.number.isRequired,
    displayName: PropTypes.string.isRequired,
    isDeprecated: PropTypes.bool
  }).isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default ModifyDatasourceDeprecatedForm
